import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { GlobalContext } from "../../../../../Context/GlobalContext";
import useCustomerDetails from "../../../../Services/Customer/useCustomerDetails";
import useGoogleLocationApi from "../../../../Services/Customer/useGoogleLocationApi";
import DeliveryLeftPanel from "./DeliveryLeftPanel";
import DeliveryMapPanel from "./DeliveryMapPanel";
import DeliveryRouteInput from "./DeliveryRouteInput";
import DeliveryTextInput from "./DeliveryTextInput";

const CITY_COORDINATES = {
  Delhi: {
    lat: 28.6139,
    lng: 77.209,
  },
  Noida: {
    lat: 28.5355,
    lng: 77.391,
  },
  Gaziabad: {
    lat: 28.6692,
    lng: 77.4538,
  },
  Ghaziabad: {
    lat: 28.6692,
    lng: 77.4538,
  },
  Gurugram: {
    lat: 28.4595,
    lng: 77.0266,
  },
};

const DeliveryCard = ({ onNext, formData, setFormData }) => {
  const { latitude, longitude, userCode } = useContext(GlobalContext);
  const location = useLocation();

  const selectedCity =
    location?.state?.city ||
    formData?.city ||
    sessionStorage.getItem("selectedCity") ||
    "Delhi";

  const selectedVehicle =
    location?.state?.vehicle ||
    formData?.vehicle ||
    sessionStorage.getItem("selectedVehicle") ||
    "";

  const selectedCityCenter = useMemo(() => {
    return CITY_COORDINATES[selectedCity] || CITY_COORDINATES.Delhi;
  }, [selectedCity]);

  const {
    getFavouriteAddressApi,
    getFavouriteAddressLoading,
    getPackerSlotDetailsApi,
    getPackerSlotDetailsLoading,
  } = useCustomerDetails();

  const {
    placesAutoCompletionApi,
    placesAutoCompletionLoading,
    getLatLongByPlaceIdApi,
    getLatLongByPlaceIdLoading,
  } = useGoogleLocationApi();

  const mapContainerRef = useRef(null);
  const googleMapRef = useRef(null);
  const markerRef = useRef(null);
  const searchTimerRef = useRef(null);
  const autoLocationSetRef = useRef(false);
  const activeAddressFieldRef = useRef("pickupAddress");
  const suggestionsWrapperRef = useRef(null);
  const mapIdleTimerRef = useRef(null);
  const isUserMovingMapRef = useRef(false);
  const skipNextIdleRef = useRef(true);
  const lastCenterSelectedRef = useRef("");

  const [deliveryType, setDeliveryType] = useState(
    formData?.deliveryType || "instant",
  );

  const [errors, setErrors] = useState({});
  const [activeAddressField, setActiveAddressField] = useState("pickupAddress");
  const [placeSuggestions, setPlaceSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [resolvingLocation, setResolvingLocation] = useState(false);
  const [currentLocationLoading, setCurrentLocationLoading] = useState(false);
  const [isMapMoving, setIsMapMoving] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    mobile: "",
  });

  const [selectedScheduleDate, setSelectedScheduleDate] = useState(
    formData?.scheduleDate || "",
  );

  const [packerSlotGroups, setPackerSlotGroups] = useState([]);

  useEffect(() => {
    activeAddressFieldRef.current = activeAddressField;
  }, [activeAddressField]);

  const [mapCenter, setMapCenter] = useState({
    lat: selectedCityCenter.lat,
    lng: selectedCityCenter.lng,
  });

  useEffect(() => {
    if (formData?.deliveryType) {
      setDeliveryType(formData.deliveryType);
    }
  }, [formData?.deliveryType]);

  useEffect(() => {
    const fetchPackerSlots = async () => {
      if (deliveryType !== "schedule") return;

      const sessionUser = JSON.parse(sessionStorage.getItem("user") || "{}");

      const finalUserId =
        userCode ||
        sessionUser?.userCode ||
        sessionUser?.userid ||
        sessionUser?.userId ||
        "";

      if (!finalUserId) return;

      const result = await getPackerSlotDetailsApi({
        userId: finalUserId,
      });

      const groups = normalizePackerSlotGroups(result);

      setPackerSlotGroups(groups);

      const firstAvailableGroup = groups.find((group) =>
        group.slots.some((slot) => slot.isAvailable),
      );

      if (!selectedScheduleDate && firstAvailableGroup?.date) {
        setSelectedScheduleDate(firstAvailableGroup.date);

        setFormData((prev) => ({
          ...prev,
          scheduleDate: prev?.scheduleDate || firstAvailableGroup.date,
        }));
      }
    };

    fetchPackerSlots();
  }, [deliveryType, userCode]);

  useEffect(() => {
    if (!selectedCity) return;

    sessionStorage.setItem("selectedCity", selectedCity);

    if (selectedVehicle) {
      sessionStorage.setItem("selectedVehicle", selectedVehicle);
    }

    setFormData((prev) => ({
      ...prev,
      city: prev?.city || selectedCity,
      vehicle: prev?.vehicle || selectedVehicle,
    }));
  }, [selectedCity, selectedVehicle, setFormData]);

  useEffect(() => {
    if (!selectedCity) return;

    autoLocationSetRef.current = true;

    const cityCenter = CITY_COORDINATES[selectedCity] || CITY_COORDINATES.Delhi;

    setMapCenter(cityCenter);

    setFormData((prev) => ({
      ...prev,
      city: prev?.city || selectedCity,
      vehicle: prev?.vehicle || selectedVehicle,
    }));

    if (googleMapRef.current) {
      moveMapToCity(selectedCity);
    }
  }, [selectedCity, selectedVehicle, setFormData]);

  useEffect(() => {
    loadGoogleMap();

    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }

      if (mapIdleTimerRef.current) {
        clearTimeout(mapIdleTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsWrapperRef.current &&
        !suggestionsWrapperRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    const handleEscKey = (event) => {
      if (event.key === "Escape") {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscKey);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscKey);
    };
  }, []);

  useEffect(() => {
    try {
      const sessionUser = JSON.parse(sessionStorage.getItem("user") || "{}");
      const loginMobile = sessionStorage.getItem("login_mobile") || "";

      const firstName =
        sessionUser?.firstName ||
        sessionUser?.first_name ||
        formData?.firstName ||
        "";

      const lastName =
        sessionUser?.lastName ||
        sessionUser?.last_name ||
        formData?.lastName ||
        "";

      const mobile =
        sessionUser?.mobile ||
        sessionUser?.phone ||
        sessionUser?.mobileNumber ||
        sessionUser?.customerMobile ||
        loginMobile ||
        formData?.senderMobile ||
        "";

      const fullName = `${firstName} ${lastName}`.trim();

      setCustomerInfo({
        name: fullName,
        mobile,
      });

      setFormData((prev) => ({
        ...prev,
        senderName: prev?.senderName || fullName,
        senderMobile:
          prev?.senderMobile ||
          String(mobile || "")
            .replace(/\D/g, "")
            .slice(0, 10),
      }));
    } catch (error) {
      console.error("Session user parse error:", error);
    }
  }, []);

  const loadGoogleMap = () => {
    const googleKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    if (!googleKey) {
      console.error("Google Maps API key missing");
      return;
    }

    if (window.google?.maps) {
      initMap();
      return;
    }

    const existingScript = document.getElementById("google-maps-script");

    if (existingScript) {
      existingScript.addEventListener("load", initMap);
      return;
    }

    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleKey}&libraries=places,geometry`;
    script.async = true;
    script.defer = true;
    script.onload = initMap;

    document.body.appendChild(script);
  };

  const initMap = () => {
    if (!mapContainerRef.current || !window.google?.maps) return;
    if (googleMapRef.current) return;

    const initialCenter = selectedCityCenter;

    googleMapRef.current = new window.google.maps.Map(mapContainerRef.current, {
      center: initialCenter,
      zoom: 12,
      disableDefaultUI: true,
      zoomControl: window.innerWidth >= 768,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,

      gestureHandling: "greedy",
      draggable: true,
      scrollwheel: true,
    });

    markerRef.current = null;

    const startUserMapMove = () => {
      isUserMovingMapRef.current = true;
      setIsMapMoving(true);
    };

    googleMapRef.current.addListener("dragstart", startUserMapMove);

    googleMapRef.current.addListener("idle", () => {
      if (!googleMapRef.current) return;

      if (skipNextIdleRef.current) {
        skipNextIdleRef.current = false;
        setIsMapMoving(false);
        return;
      }

      if (!isUserMovingMapRef.current) {
        setIsMapMoving(false);
        return;
      }

      if (mapIdleTimerRef.current) {
        clearTimeout(mapIdleTimerRef.current);
      }

      mapIdleTimerRef.current = setTimeout(async () => {
        const center = googleMapRef.current.getCenter();

        if (!center) return;

        const lat = center.lat();
        const lng = center.lng();

        setIsMapMoving(false);
        isUserMovingMapRef.current = false;

        await handleMapCenterSelect(lat, lng);
      }, 600);
    });
  };

  const moveMapToCity = (cityName) => {
    if (!window.google?.maps || !googleMapRef.current) return;

    const cityCenter = CITY_COORDINATES[cityName] || CITY_COORDINATES.Delhi;

    skipNextIdleRef.current = true;

    googleMapRef.current.panTo(cityCenter);
    googleMapRef.current.setZoom(12);

    setMapCenter(cityCenter);
  };

  const moveMapToLocation = (lat, lng) => {
    if (!window.google?.maps || !googleMapRef.current) {
      return;
    }

    const position = {
      lat: Number(lat),
      lng: Number(lng),
    };

    skipNextIdleRef.current = true;

    googleMapRef.current.panTo(position);
    googleMapRef.current.setZoom(16);
  };

  const reverseGeocodeLatLng = async (lat, lng) => {
    if (!window.google?.maps) {
      console.error("Google Maps not loaded");
      return null;
    }

    try {
      const geocoder = new window.google.maps.Geocoder();

      const result = await geocoder.geocode({
        location: {
          lat: Number(lat),
          lng: Number(lng),
        },
      });

      const results = result?.results || [];

      if (!results.length) {
        console.error("No address found for:", lat, lng);
        return null;
      }

      const preferredResult =
        results.find((item) =>
          item?.types?.some((type) =>
            [
              "street_address",
              "premise",
              "subpremise",
              "establishment",
              "point_of_interest",
            ].includes(type),
          ),
        ) ||
        results.find((item) => item?.geometry?.location_type === "ROOFTOP") ||
        results[0];

      return {
        address: preferredResult?.formatted_address || "",
        placeId: preferredResult?.place_id || "",
      };
    } catch (error) {
      console.error("Reverse Geocode Error:", error);
      return null;
    }
  };

  const withTimeout = (promise, timeoutMs = 3500) => {
    return Promise.race([
      promise,
      new Promise((resolve) => {
        setTimeout(() => resolve(null), timeoutMs);
      }),
    ]);
  };

  const getNearbyPlaceName = async (lat, lng) => {
    if (!window.google?.maps || !googleMapRef.current) {
      return null;
    }

    try {
      const service = new window.google.maps.places.PlacesService(
        googleMapRef.current,
      );

      const location = new window.google.maps.LatLng(Number(lat), Number(lng));

      const request = {
        location,
        radius: 80,
      };

      return await withTimeout(
        new Promise((resolve) => {
          service.nearbySearch(request, (results, status) => {
            if (
              status !== window.google.maps.places.PlacesServiceStatus.OK ||
              !results?.length
            ) {
              resolve(null);
              return;
            }

            const sortedResults = results
              .filter((place) => place?.name && place?.place_id)
              .map((place) => {
                const placeLocation = place?.geometry?.location;

                const distance =
                  placeLocation && window.google?.maps?.geometry?.spherical
                    ? window.google.maps.geometry.spherical.computeDistanceBetween(
                        location,
                        placeLocation,
                      )
                    : 999999;

                return {
                  ...place,
                  distance,
                };
              })
              .sort((a, b) => a.distance - b.distance);

            const nearestPlace = sortedResults[0];

            if (!nearestPlace?.place_id) {
              resolve(null);
              return;
            }

            service.getDetails(
              {
                placeId: nearestPlace.place_id,
                fields: ["name", "formatted_address", "vicinity", "place_id"],
              },
              (placeDetails, detailsStatus) => {
                if (
                  detailsStatus ===
                    window.google.maps.places.PlacesServiceStatus.OK &&
                  placeDetails
                ) {
                  resolve({
                    name: placeDetails?.name || nearestPlace?.name || "",
                    formattedAddress:
                      placeDetails?.formatted_address ||
                      nearestPlace?.vicinity ||
                      "",
                    vicinity:
                      placeDetails?.vicinity || nearestPlace?.vicinity || "",
                    placeId:
                      placeDetails?.place_id || nearestPlace?.place_id || "",
                  });

                  return;
                }

                resolve({
                  name: nearestPlace?.name || "",
                  formattedAddress: nearestPlace?.vicinity || "",
                  vicinity: nearestPlace?.vicinity || "",
                  placeId: nearestPlace?.place_id || "",
                });
              },
            );
          });
        }),
        3500,
      );
    } catch (error) {
      console.error("Nearby Place Error:", error);
      return null;
    }
  };

  const getDisplayAddressFromLatLng = async (lat, lng) => {
    const [nearbyPlace, geocodeResult] = await Promise.all([
      getNearbyPlaceName(lat, lng),
      reverseGeocodeLatLng(lat, lng),
    ]);

    const geocodeAddress = geocodeResult?.address || "";

    if (nearbyPlace?.name) {
      const placeName = nearbyPlace.name;
      const placeAddress =
        nearbyPlace.formattedAddress ||
        nearbyPlace.vicinity ||
        geocodeAddress ||
        "";

      if (
        placeAddress &&
        !placeAddress.toLowerCase().includes(placeName.toLowerCase())
      ) {
        return `${placeName}, ${placeAddress}`;
      }

      return placeAddress || placeName;
    }

    // fallback: agar nearby place nahi mila, to geocoder ka full address use karo
    return geocodeAddress || "";
  };

  const getLatLongFromAddressText = async (addressText) => {
    if (!addressText) return null;

    const googleKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    if (!googleKey) {
      console.error("Google Maps API key missing");
      return null;
    }

    try {
      setResolvingLocation(true);

      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          `${addressText}, India`,
        )}&key=${googleKey}`,
      );

      const data = await response.json();

      if (data?.status === "OK" && data?.results?.length > 0) {
        const selectedPlace = data.results[0];

        const lat = selectedPlace?.geometry?.location?.lat;
        const lng = selectedPlace?.geometry?.location?.lng;

        if (!lat || !lng) return null;

        return {
          address: selectedPlace?.formatted_address || addressText,
          latitude: String(lat),
          longitude: String(lng),
        };
      }

      return null;
    } catch (error) {
      console.error("Geocode Error:", error);
      return null;
    } finally {
      setResolvingLocation(false);
    }
  };

  const selectedDateGroup = useMemo(() => {
    return packerSlotGroups.find(
      (group) => group.date === selectedScheduleDate,
    );
  }, [packerSlotGroups, selectedScheduleDate]);

  const visibleScheduleSlots = selectedDateGroup?.slots || [];

  const extractPlaceSuggestions = (apiResponse) => {
    const data =
      apiResponse?.response ||
      apiResponse?.data ||
      apiResponse?.result ||
      apiResponse?.predictions ||
      apiResponse?.suggestions ||
      [];

    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.predictions)) return data.predictions;
    if (Array.isArray(data?.suggestions)) return data.suggestions;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.response)) return data.response;

    return [];
  };

  const normalizeSuggestion = (item) => {
    const address =
      item?.description ||
      item?.formatted_address ||
      item?.address ||
      item?.name ||
      item?.main_text ||
      item?.structured_formatting?.main_text ||
      item?.terms?.map((term) => term?.value).join(", ") ||
      "";

    const secondaryText =
      item?.secondary_text ||
      item?.structured_formatting?.secondary_text ||
      item?.subtitle ||
      "";

    const lat =
      item?.latitude ||
      item?.lat ||
      item?.location?.lat ||
      item?.location?.latitude ||
      item?.geometry?.location?.lat ||
      item?.geometry?.location?.latitude ||
      "";

    const lng =
      item?.longitude ||
      item?.lng ||
      item?.long ||
      item?.location?.lng ||
      item?.location?.longitude ||
      item?.geometry?.location?.lng ||
      item?.geometry?.location?.longitude ||
      "";

    return {
      ...item,
      displayAddress: address,
      secondaryText,
      latitude: lat,
      longitude: lng,
      placeId: item?.place_id || item?.placeId || "",
    };
  };

  const normalizeLatLongByPlaceIdResponse = (apiResponse) => {
    const data =
      apiResponse?.response ||
      apiResponse?.data ||
      apiResponse?.result ||
      apiResponse ||
      {};

    const lat =
      data?.latitude ||
      data?.lat ||
      data?.location?.lat ||
      data?.geometry?.location?.lat ||
      "";

    const lng =
      data?.longitude ||
      data?.lng ||
      data?.long ||
      data?.location?.lng ||
      data?.geometry?.location?.lng ||
      "";

    const address =
      data?.address ||
      data?.formatted_address ||
      data?.formattedAddress ||
      data?.description ||
      data?.name ||
      "";

    return {
      latitude: lat,
      longitude: lng,
      address,
    };
  };

  const normalizeFavouriteAddress = (item) => {
    const address =
      item?.address ||
      item?.fullAddress ||
      item?.formatted_address ||
      item?.locationAddress ||
      item?.favouriteAddress ||
      "";

    const lat =
      item?.latitude ||
      item?.lat ||
      item?.location_lat ||
      item?.location?.lat ||
      "";

    const lng =
      item?.longitude ||
      item?.lng ||
      item?.location_lng ||
      item?.location?.lng ||
      "";

    return {
      ...item,
      isFavourite: true,
      displayAddress: address,
      secondaryText:
        item?.favourite || item?.tag || item?.type || "Saved address",
      latitude: lat,
      longitude: lng,
    };
  };

  const extractFavouriteAddresses = (apiResponse) => {
    const data =
      apiResponse?.response || apiResponse?.data || apiResponse?.result || [];

    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.response)) return data.response;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.addresses)) return data.addresses;

    return [];
  };

  const getLatKeyName = (fieldName) => {
    return fieldName === "pickupAddress" ? "pickupLatitude" : "dropLatitude";
  };

  const getLngKeyName = (fieldName) => {
    return fieldName === "pickupAddress" ? "pickupLongitude" : "dropLongitude";
  };

  const updateAddressWithLocation = ({
    fieldName,
    address,
    lat,
    lng,
    shouldMoveMap = true,
  }) => {
    const latKey = getLatKeyName(fieldName);
    const lngKey = getLngKeyName(fieldName);

    setFormData((prev) => ({
      ...prev,
      [fieldName]: address || prev?.[fieldName] || "",
      [latKey]: String(lat || ""),
      [lngKey]: String(lng || ""),
    }));

    setMapCenter({
      lat: Number(lat),
      lng: Number(lng),
    });

    if (shouldMoveMap) {
      moveMapToLocation(lat, lng);
    }
  };

  const handleMapLocationSelect = async (lat, lng) => {
    const selectedField = activeAddressFieldRef.current || "pickupAddress";

    setResolvingLocation(true);

    try {
      const finalAddress = await getDisplayAddressFromLatLng(lat, lng);

      const safeAddress =
        finalAddress || `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;

      updateAddressWithLocation({
        fieldName: selectedField,
        address: safeAddress,
        lat,
        lng,
        shouldMoveMap: true,
      });

      setErrors((prev) => ({
        ...prev,
        [selectedField]: "",
      }));
    } catch (error) {
      console.error("Address not found for selected location:", error);
    } finally {
      setResolvingLocation(false);
    }
  };

  const handleMapCenterSelect = async (lat, lng) => {
    const selectedField = activeAddressFieldRef.current || "pickupAddress";

    const roundedLat = Number(lat).toFixed(6);
    const roundedLng = Number(lng).toFixed(6);
    const centerKey = `${selectedField}_${roundedLat}_${roundedLng}`;

    if (lastCenterSelectedRef.current === centerKey) {
      return;
    }

    lastCenterSelectedRef.current = centerKey;

    setResolvingLocation(true);

    try {
      const finalAddress = await getDisplayAddressFromLatLng(lat, lng);

      const safeAddress =
        finalAddress || `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;

      updateAddressWithLocation({
        fieldName: selectedField,
        address: safeAddress,
        lat,
        lng,
        shouldMoveMap: false,
      });

      setErrors((prev) => ({
        ...prev,
        [selectedField]: "",
      }));
    } catch (error) {
      console.error("Map center select error:", error);

      updateAddressWithLocation({
        fieldName: selectedField,
        address: `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`,
        lat,
        lng,
        shouldMoveMap: false,
      });
    } finally {
      setResolvingLocation(false);
      setIsMapMoving(false);
      isUserMovingMapRef.current = false;
    }
  };

  const handleShowRecentAddresses = async (fieldName) => {
    const finalUserCode =
      userCode ||
      JSON.parse(sessionStorage.getItem("user") || "{}")?.userCode ||
      "";

    if (!finalUserCode) return;

    const result = await getFavouriteAddressApi({
      userId: finalUserCode,
    });

    const favouriteSuggestions = extractFavouriteAddresses(result).map(
      normalizeFavouriteAddress,
    );

    if (favouriteSuggestions.length > 0) {
      setActiveAddressField(fieldName);
      activeAddressFieldRef.current = fieldName;
      setPlaceSuggestions(favouriteSuggestions);
      setShowSuggestions(true);
    }
  };

  const handleSearchPlaces = async (value) => {
    if (!value || value.trim().length < 3) {
      setPlaceSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const finalUserCode =
      userCode ||
      JSON.parse(sessionStorage.getItem("user") || "{}")?.userCode ||
      "";

    const [favouriteResult, placesResult] = await Promise.all([
      finalUserCode
        ? getFavouriteAddressApi({
            userId: finalUserCode,
          })
        : Promise.resolve(null),

      placesAutoCompletionApi({
        input: value,
        latitude: Number(mapCenter.lat || selectedCityCenter.lat || 0),
        longitude: Number(mapCenter.lng || selectedCityCenter.lng || 0),
        radius: 50000,
      }),
    ]);

    const searchText = value.toLowerCase();

    const favouriteSuggestions = extractFavouriteAddresses(favouriteResult)
      .map(normalizeFavouriteAddress)
      .filter((item) =>
        item?.displayAddress?.toLowerCase().includes(searchText),
      );

    const googleSuggestions =
      extractPlaceSuggestions(placesResult).map(normalizeSuggestion);

    setPlaceSuggestions([...favouriteSuggestions, ...googleSuggestions]);
    setShowSuggestions(true);
  };

  const updateField = (name, value) => {
    let finalValue = value;

    if (name === "senderMobile" || name === "receiverMobile") {
      finalValue = value.replace(/\D/g, "").slice(0, 10);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: finalValue,
    }));

    if (name === "senderMobile" || name === "receiverMobile") {
      setErrors((prev) => ({
        ...prev,
        [name]:
          finalValue.length === 0
            ? "Mobile number is required"
            : finalValue.length !== 10
              ? "Mobile number must be 10 digits"
              : "",
      }));
    }
  };

  const updateRouteField = (name, value) => {
    setActiveAddressField(name);

    setFormData((prev) => ({
      ...prev,
      [name]: value,
      [getLatKeyName(name)]: "",
      [getLngKeyName(name)]: "",
    }));

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    searchTimerRef.current = setTimeout(() => {
      handleSearchPlaces(value);
    }, 450);
  };

  const handleSelectSuggestion = async (suggestion) => {
    const selected = suggestion?.isFavourite
      ? normalizeFavouriteAddress(suggestion)
      : normalizeSuggestion(suggestion);

    const selectedAddress =
      selected.displayAddress || formData?.[activeAddressField] || "";

    setShowSuggestions(false);
    setPlaceSuggestions([]);

    // Favourite address already has lat/lng
    if (selected.isFavourite && selected.latitude && selected.longitude) {
      updateAddressWithLocation({
        fieldName: activeAddressField,
        address: selectedAddress,
        lat: selected.latitude,
        lng: selected.longitude,
        shouldMoveMap: true,
      });

      return;
    }

    // Google suggestion: prefer placeId API
    if (selected.placeId) {
      const placeResult = await getLatLongByPlaceIdApi({
        placeId: selected.placeId,
      });

      const resolvedPlace = normalizeLatLongByPlaceIdResponse(placeResult);

      if (resolvedPlace.latitude && resolvedPlace.longitude) {
        const finalAddress = resolvedPlace.address || selectedAddress;

        updateAddressWithLocation({
          fieldName: activeAddressField,
          address: finalAddress,
          lat: resolvedPlace.latitude,
          lng: resolvedPlace.longitude,
          shouldMoveMap: true,
        });

        return;
      }
    }

    // Fallback: if suggestion already contains lat/lng
    if (selected.latitude && selected.longitude) {
      updateAddressWithLocation({
        fieldName: activeAddressField,
        address: selectedAddress,
        lat: selected.latitude,
        lng: selected.longitude,
        shouldMoveMap: true,
      });

      return;
    }

    // Final fallback: address text geocode
    const resolved = await getLatLongFromAddressText(selectedAddress);

    if (resolved) {
      updateAddressWithLocation({
        fieldName: activeAddressField,
        address: resolved.address,
        lat: resolved.latitude,
        lng: resolved.longitude,
        shouldMoveMap: true,
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        [activeAddressField]: selectedAddress,
      }));
    }
  };

  const handleUseCurrentLocation = async () => {
    skipNextIdleRef.current = true;

    if (latitude && longitude) {
      await handleMapLocationSelect(latitude, longitude);
      return;
    }

    if (!navigator.geolocation) {
      console.error("Geolocation not supported");
      return;
    }

    setCurrentLocationLoading(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        await handleMapLocationSelect(
          position.coords.latitude,
          position.coords.longitude,
        );

        setCurrentLocationLoading(false);
      },
      (error) => {
        console.error("Use current location error:", error.message);
        setCurrentLocationLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      },
    );
  };

  const handleDeliveryTypeChange = (type) => {
    setDeliveryType(type);

    setFormData((prev) => ({
      ...prev,
      deliveryType: type,
    }));
  };

  const extractPackerSlotGroups = (apiResponse) => {
    const data =
      apiResponse?.data || apiResponse?.response || apiResponse?.result || [];

    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.response)) return data.response;

    return [];
  };

  const normalizePackerSlotGroups = (apiResponse) => {
    return extractPackerSlotGroups(apiResponse).map((group) => ({
      date: String(group?.date || ""),
      slots: Array.isArray(group?.slotDetails)
        ? group.slotDetails.map((slot) => {
            const slotTime = String(slot?.slot_time || "");
            const [startTime = "", endTime = ""] = slotTime.split("-");

            const formattedSlotTime = `${formatTime12Hour(startTime)} - ${formatTime12Hour(endTime)}`;

            return {
              slotId: String(slot?.pk_slot_id || ""),
              date: String(group?.date || ""),
              slotTime,
              startTime,
              endTime,
              label: formattedSlotTime,
              status: String(slot?.status || ""),
              isAvailable: String(slot?.status || "").toUpperCase() === "Y",
              createdOn: slot?.created_on || "",
              raw: slot,
            };
          })
        : [],
    }));
  };

  const formatScheduleDate = (dateValue) => {
    if (!dateValue) return "Select date";

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return dateValue;
    }

    return date.toLocaleDateString("en-IN", {
      weekday: "short",
      day: "2-digit",
      month: "short",
    });
  };

  const formatFullScheduleDate = (dateValue) => {
    if (!dateValue) return "";

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return dateValue;
    }

    return date.toLocaleDateString("en-IN", {
      weekday: "long",
      day: "2-digit",
      month: "long",
    });
  };

  const handleSelectScheduleSlot = (slot) => {
    if (!slot?.isAvailable) return;

    setSelectedScheduleDate(slot.date);

    setFormData((prev) => ({
      ...prev,
      scheduleDate: slot.date || "",
      scheduleTime: slot.startTime || "",
      scheduleEndTime: slot.endTime || "",
      scheduleSlotTime: slot.slotTime || "",
      scheduleSlotId: slot.slotId || "",
      scheduleSlotLabel: slot.label || "",
      packerSlotId: slot.slotId || "",
    }));

    setErrors((prev) => ({
      ...prev,
      scheduleDate: "",
      scheduleTime: "",
      scheduleSlotId: "",
    }));
  };

  const formatTime12Hour = (time) => {
    if (!time) return "";

    const [hours, minutes] = time.split(":");

    const date = new Date();
    date.setHours(Number(hours), Number(minutes));

    return date.toLocaleTimeString("en-IN", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.pickupAddress?.trim()) {
      newErrors.pickupAddress = "Pickup address is required";
    }

    if (!formData?.dropAddress?.trim()) {
      newErrors.dropAddress = "Drop address is required";
    }

    if (!formData?.senderName?.trim()) {
      newErrors.senderName = "Sender name is required";
    }

    if (!formData?.receiverName?.trim()) {
      newErrors.receiverName = "Receiver name is required";
    }

    if (!formData?.senderMobile?.trim()) {
      newErrors.senderMobile = "Mobile number is required";
    } else if (!/^[6-9]\d{9}$/.test(formData.senderMobile)) {
      newErrors.senderMobile = "Enter valid 10 digit mobile number";
    }

    if (!formData?.receiverMobile?.trim()) {
      newErrors.receiverMobile = "Mobile number is required";
    } else if (!/^[6-9]\d{9}$/.test(formData.receiverMobile)) {
      newErrors.receiverMobile = "Enter valid 10 digit mobile number";
    }

    if (deliveryType === "schedule") {
      if (
        !formData?.scheduleSlotId ||
        !formData?.scheduleDate ||
        !formData?.scheduleTime
      ) {
        newErrors.scheduleSlotId = "Please select a pickup slot";
      }
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

 const renderInput = ({ label, icon, placeholder, name }) => (
  <DeliveryTextInput
    label={label}
    icon={icon}
    placeholder={placeholder}
    name={name}
    value={formData?.[name] || ""}
    error={errors?.[name]}
    onChange={updateField}
  />
);

const renderRouteInput = ({ title, placeholder, icon, name }) => (
  <DeliveryRouteInput
    title={title}
    placeholder={placeholder}
    icon={icon}
    name={name}
    formData={formData}
    errors={errors}
    activeAddressField={activeAddressField}
    activeAddressFieldRef={activeAddressFieldRef}
    suggestionsWrapperRef={suggestionsWrapperRef}
    setActiveAddressField={setActiveAddressField}
    handleShowRecentAddresses={handleShowRecentAddresses}
    updateRouteField={updateRouteField}
    getLatKeyName={getLatKeyName}
    getLngKeyName={getLngKeyName}
    currentLocationLoading={currentLocationLoading}
    placesAutoCompletionLoading={placesAutoCompletionLoading}
    getFavouriteAddressLoading={getFavouriteAddressLoading}
    getLatLongByPlaceIdLoading={getLatLongByPlaceIdLoading}
    resolvingLocation={resolvingLocation}
    customerInfo={customerInfo}
    showSuggestions={showSuggestions}
    placeSuggestions={placeSuggestions}
    normalizeFavouriteAddress={normalizeFavouriteAddress}
    normalizeSuggestion={normalizeSuggestion}
    handleSelectSuggestion={handleSelectSuggestion}
  />
);

  const isSchedule = deliveryType === "schedule";
  const selectedMapAddress = formData?.[activeAddressField] || "";

  return (
    <section className="w-full px-3 pb-6 pt-8 sm:px-4 md:px-6 md:pt-12 lg:px-0 lg:pt-0">
      <div className="mx-auto mt-10 grid w-full max-w-[1020px] overflow-hidden rounded-[26px] border border-white/80 bg-white shadow-[0_22px_70px_rgba(15,23,42,0.16)] sm:mt-4 md:mt-10 lg:mt-[120px] lg:h-[800px] lg:grid-cols-[57%_43%]">
        <DeliveryLeftPanel
          isSchedule={isSchedule}
          deliveryType={deliveryType}
          handleDeliveryTypeChange={handleDeliveryTypeChange}
          getPackerSlotDetailsLoading={getPackerSlotDetailsLoading}
          packerSlotGroups={packerSlotGroups}
          selectedScheduleDate={selectedScheduleDate}
          setSelectedScheduleDate={setSelectedScheduleDate}
          setFormData={setFormData}
          formData={formData}
          visibleScheduleSlots={visibleScheduleSlots}
          handleSelectScheduleSlot={handleSelectScheduleSlot}
          formatScheduleDate={formatScheduleDate}
          formatFullScheduleDate={formatFullScheduleDate}
          errors={errors}
          handleUseCurrentLocation={handleUseCurrentLocation}
          renderRouteInput={renderRouteInput}
          renderInput={renderInput}
          validateForm={validateForm}
          onNext={onNext}
        />

        {/* RIGHT PANEL MAP */}
        <DeliveryMapPanel
          mapContainerRef={mapContainerRef}
          isMapMoving={isMapMoving}
          resolvingLocation={resolvingLocation}
          handleUseCurrentLocation={handleUseCurrentLocation}
          selectedMapAddress={selectedMapAddress}
        />
      </div>
    </section>
  );
};

export default DeliveryCard;
